import { useState, useEffect, useCallback, useRef } from 'react';
import { checkAndHealBundleSkew } from '../utils/bundleSkewGuard';
import {
  fetchRemoteVersion,
  getWebBundleMeta,
  reloadWebBundle,
} from '../utils/webBundleMeta';

const CHECK_DEBOUNCE_MS = 2000;

/**
 * On launch and window focus, compare embedded frontend_git_sha to live /version.json.
 * Force one cache-busted reload on mismatch; banner if that shot is already spent.
 */
export function useWebBundleStaleness(): { stale: boolean; refresh: () => void } {
  const [stale, setStale] = useState(false);
  const checkingRef = useRef(false);
  const lastCheckRef = useRef(0);
  const embedded = getWebBundleMeta();

  const check = useCallback(async () => {
    const now = Date.now();
    if (checkingRef.current || now - lastCheckRef.current < CHECK_DEBOUNCE_MS) return;
    checkingRef.current = true;
    lastCheckRef.current = now;

    try {
      const result = await checkAndHealBundleSkew({
        runningSha: embedded.sha || embedded.gitHash || '',
        fetchRemote: fetchRemoteVersion,
        storage: window.sessionStorage,
        reload: reloadWebBundle,
      });
      if (result === 'matched' || result === 'offline' || result === 'reloaded') {
        setStale(false);
        return;
      }
      if (result === 'bounded') setStale(true);
    } finally {
      checkingRef.current = false;
    }
  }, [embedded.sha, embedded.gitHash]);

  const refresh = useCallback(() => {
    reloadWebBundle();
  }, []);

  // Skew reload + resume checks are owned by installBundleSkewGuard() in main.jsx —
  // duplicate focus/visibility listeners caused back-to-back shell_navigation reloads.
  useEffect(() => {
    void check();
  }, [check]);

  return { stale, refresh };
}
